import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes, faSpinner, faCheck, faExclamationTriangle, faMoneyBillWave,
} from '@fortawesome/free-solid-svg-icons';
import AmountInput from '../../components/ui/AmountInput';
import { AMOUNT_DECIMALS } from '../../utils/precision';
import './RecordPaymentModal.css';

/**
 * Record a payment against an invoice: when, how much, and the bank reference.
 *
 * Marking an invoice "paid" used to be a status tick and nothing else, so the
 * list showed PAID next to PENDING — the Payment Status column reads the
 * payment date, and no code or screen had ever written one. A payment is an
 * event, so this records the event and lets the status follow.
 *
 * Most payments never reach this form: bank reconciliation already knows the
 * date, the amount and the bank's reference, and records them on confirm. This
 * is for money that arrived outside a reconciled statement.
 */
const RecordPaymentModal = ({ invoice, onClose, onRecorded, recordPayment }) => {
  const today = new Date().toISOString().slice(0, 10);
  const gross = parseFloat(invoice?.gross_amount) || 0;

  const [amount, setAmount] = useState(invoice?.gross_amount || '');
  const [paymentDate, setPaymentDate] = useState(today);
  const [reference, setReference] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const entered = parseFloat(amount) || 0;
  const shortfall = gross - entered;
  // A cent either way is rounding; anything more is a real shortfall and the
  // operator should see it BEFORE they commit, not in the response.
  const willSettle = entered > 0 && shortfall <= 0.01;

  const handleSubmit = async () => {
    if (!(parseFloat(amount) > 0)) {
      setError('Enter the amount that was actually received.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await recordPayment(invoice.id, {
        amount,
        payment_date: paymentDate,
        reference,
      });
      setResult(res);
      onRecorded(res);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not record the payment.');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="rp-overlay" onClick={onClose}>
        <div className="rp-modal" onClick={(e) => e.stopPropagation()}>
          <div className="rp-header">
            <h2><FontAwesomeIcon icon={faCheck} /> Payment recorded</h2>
            <button className="rp-close" onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <div className="rp-body">
            <p className="rp-result-message">{result.message}</p>
            <div className="rp-summary">
              <div><span>Invoice</span><span>{invoice.invoice_number}</span></div>
              <div><span>Recorded</span><span>{result.amount} {invoice.currency}</span></div>
              <div><span>Date</span><span>{result.payment_date}</span></div>
              <div><span>Status</span><span className="rp-status">{result.status}</span></div>
            </div>
          </div>
          <div className="rp-footer">
            <button className="rp-btn-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rp-overlay" onClick={onClose}>
      <div className="rp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rp-header">
          <h2><FontAwesomeIcon icon={faMoneyBillWave} /> Record Payment</h2>
          <button className="rp-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="rp-body">
          <div className="rp-invoice-card">
            <div><span>Invoice</span><strong>{invoice.invoice_number}</strong></div>
            <div><span>Customer</span><strong>{invoice.partner_name}</strong></div>
            <div>
              <span>Invoice total</span>
              <strong>{gross.toLocaleString('en-US', {
                minimumFractionDigits: 2, maximumFractionDigits: 2,
              })} {invoice.currency}</strong>
            </div>
          </div>

          <div className="rp-field">
            <label htmlFor="rp_amount">Amount received <span className="rp-required">*</span></label>
            {/* Paid money is stored to the cent (payment_amount is
                DecimalField(15, 2)): a 3rd decimal is refused here, not by
                the server after Record Payment. */}
            <AmountInput
              id="rp_amount"
              value={amount}
              onChange={setAmount}
              maxDecimals={AMOUNT_DECIMALS}
              placeholder="0.00"
            />
          </div>

          <div className="rp-row">
            <div className="rp-field">
              <label htmlFor="rp_date">Date received <span className="rp-required">*</span></label>
              <input
                id="rp_date"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
              />
            </div>
            <div className="rp-field">
              <label htmlFor="rp_reference">Bank reference <span className="rp-optional">(optional)</span></label>
              <input
                id="rp_reference"
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="e.g. UETR or bank statement ref"
              />
            </div>
          </div>

          {/* Say what will happen before they commit, not after. */}
          {entered > 0 && (
            willSettle ? (
              <div className="rp-outcome rp-outcome-settle">
                <FontAwesomeIcon icon={faCheck} />
                <span>This settles the invoice in full. It will be marked <strong>PAID</strong>.</span>
              </div>
            ) : (
              <div className="rp-outcome rp-outcome-short">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>
                  <strong>{shortfall.toLocaleString('en-US', {
                    minimumFractionDigits: 2, maximumFractionDigits: 2,
                  })} {invoice.currency}</strong> would still be outstanding, so the
                  invoice stays unpaid. The payment is still recorded.
                </span>
              </div>
            )
          )}

          {error && (
            <div className="rp-error">
              <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
            </div>
          )}
        </div>

        <div className="rp-footer">
          <button className="rp-btn-cancel" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button className="rp-btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting
              ? <><FontAwesomeIcon icon={faSpinner} spin /> Recording...</>
              : <><FontAwesomeIcon icon={faCheck} /> Record Payment</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecordPaymentModal;
